export interface RecordingOptions {
	layout: "screen" | "screen-camera" | "camera";
	microphone: boolean;
	systemAudio: boolean;
	quality: "720p" | "1080p";
	countdown: "off" | "3" | "5";
	cameraPosition: "top-left" | "top-right" | "bottom-left" | "bottom-right";
}

export interface RecordingState {
	status: "setup" | "countdown" | "recording" | "paused" | "saving" | "success" | "failed";
	duration: number;
	error?: string;
	recordedBlob?: Blob;
	recordedUrl?: string;
}

export interface AudioActivity {
	microphone: boolean;
	systemAudio: boolean;
}

export class ScreenRecording {
	private mediaRecorder: MediaRecorder | null = null;
	private stream: MediaStream | null = null;
	private recordedChunks: Blob[] = [];
	private startTime: number = 0;
	private recordingDuration: number = 0;
	private countdownTimer: NodeJS.Timeout | null = null;
	private recordingTimer: NodeJS.Timeout | null = null;
	private audioAnalyzers: {
		microphone?: AnalyserNode;
		systemAudio?: AnalyserNode;
	} = {};
	private audioActivityCallbacks: Set<(activity: AudioActivity) => void> = new Set();
	private stateChangeCallbacks: Set<(state: RecordingState) => void> = new Set();

	// Canvas compositing properties
	private canvas: HTMLCanvasElement | null = null;
	private canvasContext: CanvasRenderingContext2D | null = null;
	private renderLoopId: number | null = null;
	private screenVideoElement: HTMLVideoElement | null = null;
	private cameraVideoElement: HTMLVideoElement | null = null;
	private originalStreams: {
		screenStream: MediaStream | null;
		cameraStream: MediaStream | null;
		micStream: MediaStream | null;
	} = { screenStream: null, cameraStream: null, micStream: null };

	async startRecording({
		options,
		onStateChange,
		onAudioActivity,
	}: {
		options: RecordingOptions;
		onStateChange: (state: RecordingState) => void;
		onAudioActivity: (activity: AudioActivity) => void;
	}): Promise<void> {
		try {
			this.stateChangeCallbacks.add(onStateChange);
			this.audioActivityCallbacks.add(onAudioActivity);

			// Handle countdown
			if (options.countdown !== "off") {
				await this.handleCountdown({
					seconds: parseInt(options.countdown),
					onStateChange,
				});
			}

			// Get media streams
			const streams = await this.getMediaStreams(options);

			// Store original streams for cleanup
			this.originalStreams = {
				screenStream: streams.screenStream,
				cameraStream: streams.cameraStream,
				micStream: streams.micStream,
			};

			// Keep the nullable streams in local variables so TypeScript can retain
			// the runtime narrowing inside the compositing branch.
			const screenStream = streams.screenStream;
			const cameraStream = streams.cameraStream;

			if (options.layout === "screen-camera" && screenStream && cameraStream) {
				// Set up canvas compositing
				this.stream = await this.setupCanvasCompositing({
					screenStream,
					cameraStream,
					audioTracks: streams.audioTracks,
					quality: options.quality,
					cameraPosition: options.cameraPosition,
				});
				console.log("[Screen Recording] Using canvas compositing for screen+camera recording");
			} else {
				// Combine streams directly (no compositing needed)
				console.log("[Screen Recording] Video tracks captured:", streams.videoTracks.length);
				console.log("[Screen Recording] Audio tracks captured:", streams.audioTracks.length);
				streams.videoTracks.forEach((track, i) => {
					console.log(`[Screen Recording] Track ${i}:`, track.kind, track.label);
				});

				this.stream = new MediaStream([
					...streams.videoTracks,
					...streams.audioTracks,
				]);

				console.log("[Screen Recording] Final stream video tracks:", this.stream.getVideoTracks().length);
				console.log("[Screen Recording] Final stream audio tracks:", this.stream.getAudioTracks().length);
			}

			// Setup audio analyzers for activity detection
			this.setupAudioAnalyzers(streams);

			// Create MediaRecorder
			console.log("[Screen Recording] Creating MediaRecorder with stream:");
			console.log("[Screen Recording] - Video tracks:", this.stream.getVideoTracks().length);
			console.log("[Screen Recording] - Audio tracks:", this.stream.getAudioTracks().length);
			console.log("[Screen Recording] - MIME type:", this.getSupportedMimeType());

			this.mediaRecorder = new MediaRecorder(this.stream, {
				mimeType: this.getSupportedMimeType(),
				videoBitsPerSecond: options.quality === "1080p" ? 5000000 : 2500000,
			});

			// Setup recording handlers
			this.setupRecordingHandlers();

			// Start recording
			this.mediaRecorder.start(1000); // Collect data every second
			this.startTime = Date.now();
			this.startRecordingTimer(onStateChange);

			onStateChange({
				status: "recording",
				duration: 0,
			});
		} catch (error) {
			onStateChange({
				status: "failed",
				duration: 0,
				error: error instanceof Error ? error.message : String(error),
			});
		}
	}

	pauseRecording(): void {
		if (this.mediaRecorder && this.mediaRecorder.state === "recording") {
			this.mediaRecorder.pause();
			if (this.recordingTimer) {
				clearInterval(this.recordingTimer);
			}
		}
	}

	resumeRecording(): void {
		if (this.mediaRecorder && this.mediaRecorder.state === "paused") {
			this.mediaRecorder.resume();
			this.startRecordingTimer();
		}
	}

	async stopRecording(): Promise<void> {
		return new Promise((resolve, reject) => {
			if (this.mediaRecorder && this.mediaRecorder.state !== "inactive") {
				this.mediaRecorder.onstop = () => {
					this.cleanup();
					resolve();
				};
				this.mediaRecorder.onerror = (event) => {
					this.cleanup();
					reject(event);
				};
				this.mediaRecorder.stop();
			} else {
				this.cleanup();
				resolve();
			}
		});
	}

	abortRecording(): void {
		if (this.mediaRecorder && this.mediaRecorder.state !== "inactive") {
			this.mediaRecorder.stop();
		}
		this.cleanup();
	}

	getRecording(): { blob: Blob; url: string } | null {
		if (this.recordedChunks.length === 0) return null;

		const blob = new Blob(this.recordedChunks, {
			type: this.getSupportedMimeType(),
		});
		const url = URL.createObjectURL(blob);

		return { blob, url };
	}

	private async handleCountdown({
		seconds,
		onStateChange,
	}: {
		seconds: number;
		onStateChange: (state: RecordingState) => void;
	}): Promise<void> {
		return new Promise((resolve) => {
			let remaining = seconds;
			onStateChange({
				status: "countdown",
				duration: 0,
			});

			this.countdownTimer = setInterval(() => {
				remaining--;
				if (remaining <= 0) {
					if (this.countdownTimer) {
						clearInterval(this.countdownTimer);
					}
					resolve();
				}
			}, 1000);
		});
	}

	private async getMediaStreams(options: RecordingOptions): Promise<{
		videoTracks: MediaStreamTrack[];
		audioTracks: MediaStreamTrack[];
		screenStream: MediaStream | null;
		cameraStream: MediaStream | null;
		micStream: MediaStream | null;
	}> {
		const videoTracks: MediaStreamTrack[] = [];
		const audioTracks: MediaStreamTrack[] = [];
		let screenStream: MediaStream | null = null;
		let cameraStream: MediaStream | null = null;
		let micStream: MediaStream | null = null;

		// Get screen/camera stream
		if (options.layout !== "camera") {
			try {
				screenStream = await navigator.mediaDevices.getDisplayMedia({
					video: {
						width: options.quality === "1080p" ? 1920 : 1280,
						height: options.quality === "1080p" ? 1080 : 720,
						frameRate: 30,
					},
					audio: options.systemAudio,
				});

				videoTracks.push(...screenStream.getVideoTracks());
				if (options.systemAudio) {
					audioTracks.push(...screenStream.getAudioTracks());
				}

				// Handle user stopping the screen share
				screenStream.getVideoTracks()[0].onended = () => {
					if (this.mediaRecorder && this.mediaRecorder.state === "recording") {
						this.stopRecording();
					}
				};
			} catch (_error) {
				throw new Error("Failed to capture screen. Please ensure you grant permission.");
			}
		}

		// Get camera stream
		if (options.layout !== "screen") {
			try {
				cameraStream = await navigator.mediaDevices.getUserMedia({
					video: {
						width: options.quality === "1080p" ? 1920 : 1280,
						height: options.quality === "1080p" ? 1080 : 720,
						frameRate: 30,
					},
				});
				videoTracks.push(...cameraStream.getVideoTracks());
			} catch (_error) {
				if (options.layout === "camera") {
					throw new Error("Failed to access camera. Please ensure you grant permission.");
				}
			}
		}

		// Get microphone stream
		if (options.microphone) {
			try {
				micStream = await navigator.mediaDevices.getUserMedia({
					audio: {
						echoCancellation: true,
						noiseSuppression: true,
						sampleRate: 44100,
					},
				});
				audioTracks.push(...micStream.getAudioTracks());
			} catch (error) {
				console.warn("Failed to access microphone:", error);
			}
		}

		return { videoTracks, audioTracks, screenStream, cameraStream, micStream };
	}

	private async setupCanvasCompositing(config: {
		screenStream: MediaStream;
		cameraStream: MediaStream;
		audioTracks: MediaStreamTrack[];
		quality: "720p" | "1080p";
		cameraPosition: "top-left" | "top-right" | "bottom-left" | "bottom-right";
	}): Promise<MediaStream> {
		const { screenStream, cameraStream, audioTracks, quality, cameraPosition } = config;

		// Determine canvas size based on quality
		const canvasWidth = quality === "1080p" ? 1920 : 1280;
		const canvasHeight = quality === "1080p" ? 1080 : 720;

		// Create canvas and context
		this.canvas = document.createElement("canvas");
		this.canvas.width = canvasWidth;
		this.canvas.height = canvasHeight;
		this.canvasContext = this.canvas.getContext("2d");

		if (!this.canvasContext) {
			throw new Error("Failed to get canvas context for video compositing");
		}

		// Create video elements for screen and camera
		this.screenVideoElement = document.createElement("video");
		this.screenVideoElement.autoplay = true;
		this.screenVideoElement.muted = true;
		this.screenVideoElement.srcObject = screenStream;

		this.cameraVideoElement = document.createElement("video");
		this.cameraVideoElement.autoplay = true;
		this.cameraVideoElement.muted = true;
		this.cameraVideoElement.srcObject = cameraStream;

		// Wait for videos to be ready
		await Promise.all([
			new Promise<void>((resolve) => {
				this.screenVideoElement!.onloadedmetadata = () => {
					this.screenVideoElement!.play();
					resolve();
				};
			}),
			new Promise<void>((resolve) => {
				this.cameraVideoElement!.onloadedmetadata = () => {
					this.cameraVideoElement!.play();
					resolve();
				};
			}),
		]);

		// Start render loop
		this.startCanvasRenderLoop(cameraPosition);

		// Get canvas stream and add audio tracks
		const canvasStream = this.canvas.captureStream(30); // 30 FPS
		audioTracks.forEach((track) => canvasStream.addTrack(track));

		return canvasStream;
	}

	private startCanvasRenderLoop(cameraPosition: "top-left" | "top-right" | "bottom-left" | "bottom-right"): void {
		if (!this.canvasContext || !this.screenVideoElement || !this.cameraVideoElement) {
			return;
		}

		const render = () => {
			if (!this.canvasContext || !this.screenVideoElement || !this.cameraVideoElement) {
				return;
			}

			// Draw screen video as background
			this.canvasContext.drawImage(
				this.screenVideoElement,
				0,
				0,
				this.canvas!.width,
				this.canvas!.height
			);

			// Calculate camera size and position
			const cameraSize = Math.min(this.canvas!.width, this.canvas!.height) * 0.25; // 25% of canvas
			let x = 0;
			let y = 0;
			const padding = 20;

			switch (cameraPosition) {
				case "top-left":
					x = padding;
					y = padding;
					break;
				case "top-right":
					x = this.canvas!.width - cameraSize - padding;
					y = padding;
					break;
				case "bottom-left":
					x = padding;
					y = this.canvas!.height - cameraSize - padding;
					break;
				case "bottom-right":
					x = this.canvas!.width - cameraSize - padding;
					y = this.canvas!.height - cameraSize - padding;
					break;
			}

			// Draw rounded rectangle for camera background
			this.drawRoundedRect(
				this.canvasContext,
				x - 4,
				y - 4,
				cameraSize + 8,
				cameraSize + 8,
				12
			);

			// Draw camera video
			this.canvasContext.drawImage(
				this.cameraVideoElement,
				x,
				y,
				cameraSize,
				cameraSize
			);

			// Continue render loop
			this.renderLoopId = requestAnimationFrame(render);
		};

		// Start the render loop
		render();
	}

	private drawRoundedRect(
		ctx: CanvasRenderingContext2D,
		x: number,
		y: number,
		width: number,
		height: number,
		radius: number
	): void {
		ctx.beginPath();
		ctx.moveTo(x + radius, y);
		ctx.lineTo(x + width - radius, y);
		ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
		ctx.lineTo(x + width, y + height - radius);
		ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
		ctx.lineTo(x + radius, y + height);
		ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
		ctx.lineTo(x, y + radius);
		ctx.quadraticCurveTo(x, y, x + radius, y);
		ctx.closePath();
		ctx.fillStyle = "rgba(0, 0, 0, 0.8)";
		ctx.fill();
		ctx.strokeStyle = "rgba(255, 255, 255, 0.3)";
		ctx.lineWidth = 2;
		ctx.stroke();
	}

	private setupAudioAnalyzers(streams: {
		videoTracks: MediaStreamTrack[];
		audioTracks: MediaStreamTrack[];
	}): void {
		// Create audio context for analyzing activity
		const audioContext = new AudioContext();

		streams.audioTracks.forEach((track) => {
			const stream = new MediaStream([track]);
			const source = audioContext.createMediaStreamSource(stream);
			const analyser = audioContext.createAnalyser();
			analyser.fftSize = 256;
			source.connect(analyser);

			// Determine if this is microphone or system audio (simplified)
			this.audioAnalyzers.microphone = analyser;
		});

		// Start monitoring audio activity
		this.monitorAudioActivity();
	}

	private monitorAudioActivity(): void {
		const checkActivity = () => {
			const activity: AudioActivity = {
				microphone: false,
				systemAudio: false,
			};

			if (this.audioAnalyzers.microphone) {
				const dataArray = new Uint8Array(this.audioAnalyzers.microphone.frequencyBinCount);
				this.audioAnalyzers.microphone.getByteFrequencyData(dataArray);
				activity.microphone = dataArray.some((value) => value > 0);
			}

			if (this.audioAnalyzers.systemAudio) {
				const dataArray = new Uint8Array(this.audioAnalyzers.systemAudio.frequencyBinCount);
				this.audioAnalyzers.systemAudio.getByteFrequencyData(dataArray);
				activity.systemAudio = dataArray.some((value) => value > 0);
			}

			this.audioActivityCallbacks.forEach((callback) => callback(activity));

			if (this.mediaRecorder && this.mediaRecorder.state === "recording") {
				requestAnimationFrame(checkActivity);
			}
		};

		checkActivity();
	}

	private setupRecordingHandlers(): void {
		if (!this.mediaRecorder) return;

		this.mediaRecorder.ondataavailable = (event) => {
			if (event.data.size > 0) {
				this.recordedChunks.push(event.data);
			}
		};

		this.mediaRecorder.onpause = () => {
			this.notifyStateChange({ status: "paused" });
		};

		this.mediaRecorder.onresume = () => {
			this.notifyStateChange({ status: "recording" });
		};

		this.mediaRecorder.onstop = () => {
			this.recordingDuration = Date.now() - this.startTime;
			if (this.recordingTimer) {
				clearInterval(this.recordingTimer);
			}
		};
	}

	private startRecordingTimer(onStateChange?: (state: RecordingState) => void): void {
		this.recordingTimer = setInterval(() => {
			const duration = Date.now() - this.startTime;
			if (onStateChange) {
				onStateChange({
					status: "recording",
					duration,
				});
			}
		}, 100);
	}

	private notifyStateChange({
		status,
		error,
	}: {
		status: RecordingState["status"];
		error?: string;
	}): void {
		this.stateChangeCallbacks.forEach((callback) =>
			callback({
				status,
				duration: Date.now() - this.startTime,
				error,
			})
		);
	}

	private cleanup(): void {
		if (this.countdownTimer) {
			clearInterval(this.countdownTimer);
		}
		if (this.recordingTimer) {
			clearInterval(this.recordingTimer);
		}

		// Stop render loop
		if (this.renderLoopId !== null) {
			cancelAnimationFrame(this.renderLoopId);
			this.renderLoopId = null;
		}

		// Clean up video elements
		if (this.screenVideoElement) {
			this.screenVideoElement.srcObject = null;
			this.screenVideoElement = null;
		}
		if (this.cameraVideoElement) {
			this.cameraVideoElement.srcObject = null;
			this.cameraVideoElement = null;
		}

		// Clean up canvas
		if (this.canvasContext) {
			this.canvasContext = null;
		}
		if (this.canvas) {
			this.canvas = null;
		}

		// Stop all tracks in original streams
		if (this.originalStreams.screenStream) {
			this.originalStreams.screenStream.getTracks().forEach((track) => track.stop());
		}
		if (this.originalStreams.cameraStream) {
			this.originalStreams.cameraStream.getTracks().forEach((track) => track.stop());
		}
		if (this.originalStreams.micStream) {
			this.originalStreams.micStream.getTracks().forEach((track) => track.stop());
		}
		this.originalStreams = { screenStream: null, cameraStream: null, micStream: null };

		if (this.stream) {
			this.stream.getTracks().forEach((track) => track.stop());
		}

		this.mediaRecorder = null;
		this.stream = null;
		this.audioAnalyzers = {};
		this.stateChangeCallbacks.clear();
		this.audioActivityCallbacks.clear();
	}

	private getSupportedMimeType(): string {
		const types = [
			"video/webm;codecs=vp9,opus",
			"video/webm;codecs=vp8,opus",
			"video/webm",
			"video/mp4",
		];

		for (const type of types) {
			if (MediaRecorder.isTypeSupported(type)) {
				return type;
			}
		}

		return "video/webm";
	}
}

export function formatDuration(ms: number): string {
	const seconds = Math.floor(ms / 1000);
	const minutes = Math.floor(seconds / 60);
	const remainingSeconds = seconds % 60;

	return `${minutes.toString().padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`;
}

export function getFileInfo(blob: Blob, locale = "en"): {
	size: string;
	duration: string;
	type: string;
} {
	return {
		size: formatFileSize(blob.size, locale),
		duration: "Unknown", // Duration would need to be tracked separately
		type: blob.type || "video/webm",
	};
}

export function formatFileSize(bytes: number, locale = "en"): string {
	const units = ["B", "KB", "MB", "GB"];
	let size = bytes;
	let unitIndex = 0;

	while (size >= 1024 && unitIndex < units.length - 1) {
		size /= 1024;
		unitIndex++;
	}

	return `${new Intl.NumberFormat(locale, {
		maximumFractionDigits: unitIndex === 0 ? 0 : 1,
	}).format(size)} ${units[unitIndex]}`;
}
